import { useContext, useEffect, useRef, useState } from 'react';

import { CellValueChangedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { cloneDeep } from 'lodash';
import { Toolbar } from 'primereact/toolbar';
import { useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import {
  useGetPseDay7Query,
  useLazyGetPseDay7Query,
  useUpdateFreshTransfersPSEMutation,
} from '@/app/services/excel';
import { addToast, clearToasts } from '@/app/slices/toast';
import { WithLocalStorage } from '@/components';
import { useDefaultGridOptions } from '@/grid';
import { defaultColDef, sideBar } from '@/grid/defaults';
import { TOG_PseDay7 } from '@/rtk-query.gql';
import { PrimaryContext } from '@/services';
import { useContextMenu } from '@/utils/context-menu';

import { excelStyles } from '../csr-day-7/columnDefs';
import { pseDay7ColDefs } from './columnDefs';
import { PseDay7Actions } from './PseDay7Actions';
import { PseDay7Grading } from './PseDay7Grading';
import { PseDay7Selector } from './PseDay7Selector';
import { MatingStats } from './types';

import './PseDay7.css';

export const PseDay7Page = () => {
  const defaultGridOptions = useDefaultGridOptions();
  const dispatch = useDispatch();
  const { themeMode } = useContext(PrimaryContext);
  const gridRef = useRef<AgGridReact>(null);

  const [params] = useSearchParams();
  const beginDate = params.get('beginDate');
  const endDate = params.get('endDate');
  const siteId = params.get('site');
  const accountId = params.get('account');

  const [rowData, setRowData] = useState<TOG_PseDay7[]>([]);
  const [gradeTime, setGradeTime] = useState<number>(7);
  const [graderId, setGraderId] = useState<string>('');

  const [discards, setDiscards] = useState<number>(0);
  const [degenerates, setDegenerates] = useState<number>(0);
  const [matingStats, setMatingStats] = useState<MatingStats>({
    trf: 0,
    frz: 0,
    g1: 0,
    g2: 0,
    g3: 0,
    dcg1: 0,
    dcg2: 0,
    dcg3: 0,
  });

  const [updateFreshTransfersPSE] = useUpdateFreshTransfersPSEMutation();
  const [getDay7Data] = useLazyGetPseDay7Query();
  const { data: pseDay7Data, refetch } = useGetPseDay7Query(
    {
      beginDate,
      endDate,
      siteId,
      accountId,
    },
    { skip: !beginDate || !endDate || !siteId },
  );

  useEffect(() => {
    if (pseDay7Data) {
      const filteredData = pseDay7Data.filter(
        (row) =>
          row.status === 'Implanted' ||
          row.status === 'Discarded' ||
          row.status === null,
      );

      setRowData(cloneDeep(filteredData));
    }
  }, [pseDay7Data]);

  const updateOvaDrive = async (data: TOG_PseDay7[]) => {
    const transferDay = beginDate
      ? new Date(new Date(beginDate).getTime() + 7 * 24 * 60 * 60 * 1000)
      : null;

    try {
      dispatch(
        addToast({
          severity: 'info',
          summary: 'Updating OvaDrive',
          detail: 'Please wait while we update OvaDrive',
          sticky: false,
          life: 5000,
        }),
      );
      const response = await updateFreshTransfersPSE({
        input: {
          loadDay: gradeTime.toString(),
          graderId,
          transferDay,
          transferSiteId: siteId,
          embryos: data,
        },
      }).unwrap();

      if (response?.updateFreshTransfersPse?.code === 500) {
        dispatch(
          addToast({
            severity: 'error',
            summary: 'Server Error',
            detail: response?.updateFreshTransfersPse?.message,
            life: 3000,
          }),
        );
        const day7Data = await getDay7Data({
          beginDate,
          endDate,
          siteId,
          accountId,
        }).unwrap();

        if (day7Data) {
          setRowData(cloneDeep(day7Data) as TOG_PseDay7[]);
          dispatch(clearToasts());
        }
        return false;
      }
      if (response?.updateFreshTransfersPse?.code === 200) {
        dispatch(
          addToast({
            severity: 'success',
            summary: 'Success',
            detail: response?.updateFreshTransfersPse?.message,
            life: 3000,
          }),
        );
        return true;
      }
    } finally {
      dispatch(clearToasts());
    }
  };

  const handleCellValueChanged = async (params: CellValueChangedEvent) => {
    if (params.column.getId() === 'freshStrawNo') {
      const startValue = parseInt(params.newValue);
      if (!Number.isNaN(startValue)) {
        if (params.oldValue !== null) {
          const updatedData = rowData.map((row) =>
            row === params.data ? { ...row, freshStrawNo: startValue } : row,
          );
          setRowData(updatedData);
          await updateOvaDrive([params.data]);
          return;
        }

        const allRows: TOG_PseDay7[] = [];
        params.api.forEachNodeAfterFilterAndSort((node) => {
          allRows.push(node.data);
        });

        const rowIndex = allRows.findIndex((row) => row === params.data);
        let nextNumber = startValue;

        const modifiedFertIds = new Set<string>();
        modifiedFertIds.add(params.data?.fertilizationId as string);

        const updatedData = allRows.map((row, index) => {
          if (index === rowIndex) {
            return {
              ...row,
              freshStrawNo: startValue,
            };
          } else if (
            index > rowIndex &&
            row.status !== 'Discarded' &&
            row.freshStrawNo === null
          ) {
            modifiedFertIds.add(row?.fertilizationId as string);
            return {
              ...row,
              freshStrawNo: ++nextNumber,
            };
          }
          return row;
        });

        const rowsToUpdate = updatedData.filter((row) =>
          modifiedFertIds.has(row?.fertilizationId as string),
        );

        const result = await updateOvaDrive(rowsToUpdate);
        if (result) {
          setRowData(updatedData);
        }
      }
    } else {
      const updatedData = rowData.map((row) =>
        row === params.oldValue
          ? { ...row, [params.column.getId()]: params.newValue }
          : row,
      );
      setRowData(updatedData);
      await updateOvaDrive([params.data]);
    }
  };

  const onUpdateStrawDetails = async () => {
    const selectedRows = gridRef.current?.api.getSelectedRows();
    if (!selectedRows?.length) return;

    const allRows: TOG_PseDay7[] = [];
    gridRef.current?.api.forEachNodeAfterFilterAndSort((node) => {
      allRows.push(node.data);
    });

    const firstValidRow = selectedRows.find(
      (row) => row.status !== 'Discarded',
    );
    if (!firstValidRow) return;

    let nextNumber = firstValidRow.freshStrawNo || 1;

    const updatedRows: TOG_PseDay7[] = [];
    const updatedData = allRows.map((row) => {
      if (selectedRows.includes(row) && row.status !== 'Discarded') {
        const updatedRow = {
          ...row,
          freshStrawNo: nextNumber++,
        };
        updatedRows.push(updatedRow);

        return updatedRow;
      }
      return row;
    });

    const result = await updateOvaDrive(updatedRows);
    if (result) {
      setRowData(updatedData);
    }
  };

  const { getContextMenuItems } = useContextMenu({
    idKey: 'embryoId',
  });

  return (
    <div className="common-page pseDay7">
      <Toolbar
        className="toolbar-above-panel"
        start={<PseDay7Selector setMatingStats={setMatingStats} />}
        end={
          <PseDay7Actions
            pseDay7Data={rowData as TOG_PseDay7[]}
            gridRef={gridRef}
            onUpdateStrawDetails={onUpdateStrawDetails}
            refetch={refetch}
          />
        }
      />

      <div className="pseDay7__content">
        <PseDay7Grading
          gradeTime={gradeTime}
          graderId={graderId}
          discards={discards}
          degenerates={degenerates}
          setGradeTime={setGradeTime}
          setGraderId={setGraderId}
          setDiscards={setDiscards}
          setDegenerates={setDegenerates}
          matingStats={matingStats}
        />
        <div className="pseDay7__grid">
          <WithLocalStorage storageKey="pseDay7-grid">
            <AgGridReact
              gridOptions={defaultGridOptions}
              getRowId={(params) => params.data.embryoId}
              className={`ag-theme-transova${themeMode === 'dark' ? '-dark' : ''}`}
              getContextMenuItems={getContextMenuItems}
              defaultColDef={defaultColDef}
              columnDefs={pseDay7ColDefs}
              excelStyles={excelStyles}
              rowData={rowData}
              ref={gridRef}
              onCellValueChanged={handleCellValueChanged}
              sideBar={sideBar}
              rowSelection="multiple"
            />
          </WithLocalStorage>
        </div>
      </div>
    </div>
  );
};
